import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Eye } from "lucide-react";

const SHEET_API_URL =
  "https://script.google.com/macros/s/AKfycbxnXAJfAUGK2RauSc7gUZ_fMSoOoZCZe5pHTkVi3qCPTwzQFEUzkvjwz8spKbDDU1OL/exec";
const EXCLUDED_CATEGORIES = ["Graphic Design"];

// ─── Fix Cloudinary URLs to ensure they load properly ───
const fixCloudinaryUrl = (url) => {
  if (!url) return null;

  // If it's already a working Cloudinary URL
  if (url.includes("res.cloudinary.com")) {
    // Add f_auto,q_auto for better loading, remove any broken params
    let cleanUrl = url.split("?")[0]; // Remove query params
    if (!cleanUrl.includes("/f_auto") && !cleanUrl.includes("/video/upload/")) {
      cleanUrl = cleanUrl.replace("/upload/", "/upload/f_auto,q_auto/");
    }
    return cleanUrl;
  }

  return url;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { y: 24, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 120, damping: 16 },
  },
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@600;700&family=Poppins:wght@400;500;600&display=swap');

  .rw-section { background:#fff; padding:72px 24px 64px; font-family:'Poppins',sans-serif; }
  .rw-inner { max-width:1200px; margin:0 auto; }
  .rw-label {
    display:inline-block; font-size:20px; font-weight:600; letter-spacing: 0;
    text-transform:uppercase;line-height:28px;
    background:linear-gradient(135deg,#129ADC,#B55DD0,#E154AA,#F45A39);
    -webkit-background-clip:text; -webkit-text-fill-color:transparent;
    background-clip:text; margin-bottom:10px; 
  }
  .rw-heading {
    font-family:'Space Grotesk',sans-serif;
    font-size:36px; font-weight:700;
    color:#111; margin-bottom:40px; line-height:40px;
  }
  .rw-grid {
    display:grid; grid-template-columns:repeat(3,1fr);
    gap:24px; margin-bottom:40px;
  }
  @media(max-width:700px){ .rw-grid{ grid-template-columns:1fr 1fr; } }
  @media(max-width:460px){ .rw-grid{ grid-template-columns:1fr; } }

  .rw-card {
    position:relative; background:#f0f0f0; border-radius:16px;
    overflow:hidden; cursor:pointer; aspect-ratio:4/3;
  }
  .rw-card img {
    width:100%; height:100%; object-fit:cover; display:block;
    transition:transform .4s ease;
  }
  .rw-card:hover img { transform:scale(1.04); }
  .rw-card-overlay {
    position:absolute; inset:0;
    background:linear-gradient(to top,rgba(0,0,0,0.72) 0%,rgba(0,0,0,0.1) 60%,transparent 100%);
    opacity:0; transition:opacity .3s ease;
    display:flex; flex-direction:column; justify-content:flex-end; padding:18px;
  }
  .rw-card:hover .rw-card-overlay { opacity:1; }
  .rw-card-title { font-size:14px; font-weight:600; color:#fff; margin-bottom:4px; }
  .rw-card-cat { font-size:11px; font-weight:500; letter-spacing:.12em; text-transform:uppercase; color:rgba(255,255,255,.65); }

  .rw-skeleton {
    background:linear-gradient(90deg,#f0f0f0 25%,#e4e4e4 50%,#f0f0f0 75%);
    background-size:200% 100%; animation:shimmer 1.4s infinite;
    border-radius:16px; aspect-ratio:4/3;
  }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  .rw-error {
    grid-column:1/-1; text-align:center; padding:40px 0;
    color:#e57373; font-size:14px; background:#fff5f5;
    border-radius:16px; border:1px solid #fecaca;
  }
  .rw-empty { grid-column:1/-1; text-align:center; padding:60px 0; color:#bbb; font-size:14px; }

  .rw-btns { display:flex; gap:12px; justify-content:center; flex-wrap:wrap; margin-top:20px; }
  .rw-btn-outline {
    padding:10px 30px; border-radius:100px; border:1.5px solid transparent;
    background:linear-gradient(#fff,#fff) padding-box, linear-gradient(135deg,#129ADC,#B55DD0,#E154AA,#F45A39) border-box;
    font-family:'Poppins',sans-serif; font-size:14px; font-weight:600; cursor:pointer;
  }
  .rw-btn-outline span {
    background:linear-gradient(135deg,#129ADC,#B55DD0,#E154AA,#F45A39);
    -webkit-background-clip:text; -webkit-text-fill-color:transparent;
    background-clip:text; display:inline-block;
  }
  .rw-btn-solid {
    padding:10px 30px; border-radius:100px; border:none; background:#ebebeb; color:#000000;
    font-family:'Poppins',sans-serif; font-size:14px; font-weight:600; cursor:pointer;
    display:inline-flex; align-items:center; gap:8px; transition:opacity .2s;
  }
  .rw-btn-solid:hover { opacity:.82; }
  .text-center { text-align: center; }

  /* ── Modal ── */
  .portfolio-modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.85); z-index: 9999;
    display: flex; justify-content: center; align-items: center;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  .portfolio-modal-content {
    position: relative; width: fit-content;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border-radius: 24px; display: flex; flex-direction: column;
    max-height: 90vh; overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(255, 255, 255, 0.05);
  }
  .portfolio-modal-content::before {
    content: ""; position: absolute; inset: 0; border-radius: inherit; pointer-events: none;
    background: linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 25%, transparent 50%);
    z-index: 1;
  }
  .portfolio-modal-content::after {
    content: ""; position: absolute; width: 250px; height: 250px; top: -80px; right: -80px;
    border-radius: 50%; background: radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%);
    pointer-events: none; filter: blur(30px);
  }
  .portfolio-modal-close {
    position: absolute; top: 16px; right: 16px; background: rgba(255,255,255,0.1); border: none; color: #fff;
    width: 36px; height: 36px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center;
    z-index: 10; transition: all 0.2s; backdrop-filter: blur(4px);
  }
  .portfolio-modal-close:hover { background: rgba(255,255,255,0.2); transform: scale(1.1); }
  .portfolio-modal-header { padding: 24px 32px; border-bottom: 1px solid rgba(255,255,255,0.08); flex-shrink: 0; }
  .portfolio-modal-header h3 { margin: 0; color: #fff; font-size: 1.5rem; font-weight: 600; }
  .portfolio-modal-header p { margin: 6px 0 0 0; font-size: 0.85rem; font-weight: 500; color: #B55DD0; text-transform: uppercase; letter-spacing: 1px; }
  .portfolio-modal-body { flex: 1; overflow-y: auto; overflow-x: hidden; background: #0a0a0a; scroll-behavior: smooth; }
  .portfolio-modal-body::-webkit-scrollbar { width: 8px; }
  .portfolio-modal-body::-webkit-scrollbar-track { background: #111; }
  .portfolio-modal-body::-webkit-scrollbar-thumb { background: #333; border-radius: 4px; }
  .portfolio-modal-body::-webkit-scrollbar-thumb:hover { background: #555; }
  .portfolio-modal-body img { width: 100%; height: auto; display: block; }
  .graphic-design-modal { width:auto; }
  .graphic-design-modal .portfolio-modal-body img { height: 75vh; display: block; }
  @media (max-width: 768px) { .graphic-design-modal .portfolio-modal-body img { height: 75vh; display: block; } }
`;

const RecentWorks = () => {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedImages, setFailedImages] = useState({});
  const [selectedWork, setSelectedWork] = useState(null);

  useEffect(() => {
    fetchFromSheet(false);
    const interval = setInterval(() => {
      fetchFromSheet(true);
    }, 15000); // 15 seconds polling
    return () => clearInterval(interval);
  }, []);

  const handleViewWork = (work) => {
    if (!work.viewedLocally) {
      const currentViews = Number(work.views) || 321;
      const newViews = currentViews + 1;

      setWorks(prev => prev.map(w => w.id === work.id ? { ...w, views: newViews, viewedLocally: true } : w));
      setSelectedWork({ ...work, views: newViews, viewedLocally: true });

      const safeId = work.id.replace(/[^a-zA-Z0-9_-]/g, '_');
      fetch(`https://api.counterapi.dev/v1/sintx-portfolio/${safeId}/up`)
        .catch(console.error);
    } else {
      setSelectedWork(work);
    }
  };

  const fetchFromSheet = async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await fetch(SHEET_API_URL);
      const json = await res.json();

      if (!json.success) throw new Error("Failed to load from sheet");

      const mapped = json.data
        .filter((row) => row.id && row.url)
        .filter((row) => row.deleted !== "TRUE" && row.deleted !== true)
        .map((row) => {
          let thumb = fixCloudinaryUrl(row.url);
          let mainUrl = thumb;
          if (row.url && row.url.includes("|||")) {
             const parts = row.url.split("|||");
             thumb = fixCloudinaryUrl(parts[0]);
             mainUrl = fixCloudinaryUrl(parts[1]) || thumb;
          }
          return {
            id: row.id,
            url: thumb,
            mainUrl: mainUrl,
            originalUrl: row.url,
            title: row.title || "Untitled",
            category: row.category || "UI/UX",
            views: 321,
            createdAt: row.uploadedAt
              ? new Date(row.uploadedAt).getTime()
              : Date.now(),
          };
        })
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 6);

      try {
        const counts = await Promise.allSettled(
          mapped.map(work => {
            const safeId = work.id.replace(/[^a-zA-Z0-9_-]/g, '_');
            return fetch(`https://api.counterapi.dev/v1/sintx-portfolio/${safeId}`).then(r => r.json());
          })
        );
        mapped.forEach((work, i) => {
          if (counts[i].status === 'fulfilled' && counts[i].value && typeof counts[i].value.count === 'number') {
             work.views = counts[i].value.count + 321;
          }
        });
      } catch (e) {
        console.error("Failed to fetch views", e);
      }

      setWorks(prev => {
        if (!prev || prev.length === 0) return mapped;
        return mapped.map(newWork => {
          const oldWork = prev.find(w => w.id === newWork.id);
          if (oldWork && oldWork.viewedLocally) {
            return { ...newWork, views: Math.max(newWork.views, oldWork.views), viewedLocally: true };
          }
          return newWork;
        });
      });
    } catch (err) {
      console.error("Sheet fetch failed:", err);
      if (!isBackground) setError(err.message || "Could not load works");
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const handleImageError = (id, originalUrl) => {
    console.log(`Image failed to load: ${id}, URL: ${originalUrl}`);
    setFailedImages((prev) => ({ ...prev, [id]: true }));
  };

  const getImageUrl = (work) => {
    if (failedImages[work.id]) {
      // Fallback to a placeholder or try a different format
      return `https://via.placeholder.com/400x300/1a1a2e/ffffff?text=${encodeURIComponent(work.title || "Image")}`;
    }
    return work.url;
  };

  return (
    <>
      <style>{styles}</style>
      
      {/* Modal */}
      <AnimatePresence>
        {selectedWork && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedWork(null)}
            className="portfolio-modal-overlay"
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className={`portfolio-modal-content ${
                selectedWork?.category === "Graphic Design"
                  ? "graphic-design-modal"
                  : ""
              }`}
            >
              <button onClick={() => setSelectedWork(null)} className="portfolio-modal-close">
                ✕
              </button>
              <div className="portfolio-modal-header">
                  <h3>{selectedWork.title}</h3>
                  <p>{selectedWork.category}</p>
              </div>
              <div className="portfolio-modal-body">
                 {selectedWork.category.toLowerCase() === "reels" ? (
                   <video 
                     src={selectedWork.mainUrl || selectedWork.url} 
                     controls 
                     autoPlay 
                     style={{ width: "100%", height: "auto", maxHeight: "78vh", display: "block", margin: "0 auto" }}
                   />
                 ) : (
                   <img 
                       src={selectedWork.mainUrl || selectedWork.url} 
                       alt={selectedWork.title} 
                       loading="lazy"
                   />
                 )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="rw-section" id="portfolio">
        <div className="rw-inner">
          <div className="text-center mb-14" >
            <p className="rw-label">Our Recent Works</p>
            <h2 className="rw-heading">Made with brains, not just pixels.</h2>
          </div>

          <motion.div
            className="rw-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rw-skeleton" />
              ))
            ) : error ? (
              <div className="rw-error">
                ⚠ {error} —{" "}
                <button
                  onClick={fetchFromSheet}
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    color: "inherit",
                  }}
                >
                  Retry
                </button>
              </div>
            ) : works.length === 0 ? (
              <div className="rw-empty">
                ✨ No projects yet — upload via the admin panel.
              </div>
            ) : (
              works.map((work) => (
                <motion.div
                  key={work.id}
                  variants={itemVariants}
                  className="rw-card"
                  onClick={() => handleViewWork(work)}
                >
                  <img
                    src={getImageUrl(work)}
                    alt={work.title}
                    loading="eager"
                    onError={() => handleImageError(work.id, work.originalUrl)}
                    style={{ display: "block" }}
                  />
                  <div className="rw-card-overlay">
                    <div className="rw-card-title">{work.title}</div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div className="rw-card-cat">{work.category}</div>
                      <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.65)", display: "flex", alignItems: "center", gap: "4px" }}>
                        <Eye size={12} /> {work.views || 321}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>

          <motion.div
            className="rw-btns"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.button
              className="rw-btn-outline"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => (window.location.href = "/portfolio")}
            >
              <span>See all</span>
            </motion.button>
            <motion.button
              className="rw-btn-solid"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => (window.location.href = "#contact")}
            >
              Work With Me <ArrowUpRight className="w-4 h-4" /> 
            </motion.button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default RecentWorks;
